import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { useAuthStore } from '@/store/useAuthStore';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import NotificationToasts from '@/components/NotificationToasts';
import { settingsApi } from '@/lib/api';
import { initPixels, trackPageView, isStaffPath } from '@/lib/pixels';
import { captureFirstTouch } from '@/lib/attribution';
import { AdminAppShell } from '@/components/appShell/AdminAppShell';

// Pages
import LoginPage from '@/pages/auth/LoginPage';
import SignupPage from '@/pages/auth/SignupPage';
import AuthCallbackPage from '@/pages/auth/AuthCallbackPage';
import PrinterLinkPage from '@/pages/PrinterLinkPage';
import PreviewBookPage from '@/pages/PreviewBookPage';
import CoverCatalogPage from '@/pages/CoverCatalogPage';
import CoverLibraryPage from '@/pages/customer/CoverLibraryPage';
import PhotoUploadPage from '@/pages/customer/PhotoUploadPage';
import AlbumEditorPage from '@/pages/customer/AlbumEditorPage';
import AlbumFlowPage from '@/pages/customer/AlbumFlowPage';
import ResumeLanding from '@/pages/customer/ResumeLanding';
import MagicUploadPage from '@/pages/customer/MagicUploadPage';
import ServerRenderProbe from '@/pages/internal/ServerRenderProbe';
import PreviewRenderProbe from '@/pages/internal/PreviewRenderProbe';
import QuickRenderProbe from '@/pages/internal/QuickRenderProbe';
import PdfIntakeProbe from '@/pages/internal/PdfIntakeProbe';
import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import AdminReportsPage from '@/pages/admin/AdminReportsPage';
import AdminAiComparePage from '@/pages/admin/AdminAiComparePage';
import AdminOrdersPage from '@/pages/admin/AdminOrdersPage';
import AdminOrderDetailPage from '@/pages/admin/AdminOrderDetailPage';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import AdminAssistantPage from '@/pages/admin/AdminAssistantPage';
import AdminAuditPage from '@/pages/admin/AdminAuditPage';
import AdminCustomersPage from '@/pages/admin/AdminCustomersPage';
import AdminCustomerDetailPage from '@/pages/admin/AdminCustomerDetailPage';
import AdminCoversPage from '@/pages/admin/AdminCoversPage';
import AdminSettingsPage from '@/pages/admin/AdminSettingsPage';
import AdminNotificationsPage from '@/pages/admin/AdminNotificationsPage';
import AdminLayoutTemplatesPage from '@/pages/admin/AdminLayoutTemplatesPage';
import { SlotReviewPane } from '@/pages/admin/templates/SlotReviewPane';
import AdminDeliveryAmeexPage from '@/pages/admin/AdminDeliveryAmeexPage';
import AdminFollowUpPage from '@/pages/admin/AdminFollowUpPage';
import AdminStockPage from '@/pages/admin/AdminStockPage';
import AdminProductsPage from '@/pages/admin/AdminProductsPage';
import AdminAccountingPage from '@/pages/admin/AdminAccountingPage';
import { LanguageProvider } from '@/lib/i18n';
import AdminImpressionPage from '@/pages/admin/AdminImpressionPage';

// Lazy: the parcel scanner carries a camera loop and a QR decoder (jsQR) that
// no other page needs — they load when /admin/scanner opens, never in the main
// bundle (plan 2026-10-01-parcel-scanner).
const AdminScannerPage = lazy(() => import('@/pages/admin/AdminScannerPage'));
// Coupons (2026-10-28) — lazy: designs, the box editor and pdf.js stay out of the main bundle.
const AdminCouponsPage = lazy(() => import('@/pages/admin/AdminCouponsPage'));
const AdminLivingPage = lazy(() => import('@/pages/admin/AdminLivingPage'));
// DEV ONLY: the new home + Rapports on fixture data (screenshots). The constant
// is false in a production build, so the page and its fixtures never ship.
const DevHomePreview = import.meta.env.DEV ? lazy(() => import('@/pages/dev/HomePreviewPage')) : null;
// DEV ONLY — Fournisseurs IA + Comparer les modèles on fixture data (null in a build: the import is dropped).
const DevAiProvidersPreview = import.meta.env.DEV ? lazy(() => import('@/pages/dev/AiProvidersPreviewPage')) : null;
// DEV ONLY — the Coupons screens on fixture data (null in a build: the import is dropped).
const DevCouponsPreview = import.meta.env.DEV ? lazy(() => import('@/pages/dev/CouponsPreviewPage')) : null;
const DevLivingPreview = import.meta.env.DEV ? lazy(() => import('@/pages/dev/LivingPreviewPage')) : null;
const DevOutroPreview = import.meta.env.DEV ? lazy(() => import('@/pages/dev/OutroPagePreviewPage')) : null;

function PageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/**
 * Loads pixel IDs from app_settings and injects the pixel scripts on first
 * mount; tracks PageView on every SPA route change. Settings is fetched once
 * with a long staleTime — admins changing pixel IDs need to refresh to apply.
 */
function PixelTracker() {
  const location = useLocation();

  // First-party first-touch attribution capture — runs once on app load, before
  // routing. Persists fbclid/ttclid/gclid + utm_* + real _fbc/_fbp/_ttp pixel
  // cookies into the shared `.storiz.ma` cookie so the IDs survive the
  // app→storiz.ma hop and land on the order (feeds Systems 2 + 3).
  useEffect(() => {
    captureFirstTouch();
  }, []);

  const { data } = useQuery({
    queryKey: ['app-settings-pixels'],
    queryFn: () => settingsApi.get(),
    staleTime: 10 * 60 * 1000,
  });

  // Tracking v2 (migration 268, OFF): no pixel on staff pages. Until the
  // settings arrive we do not know — nothing fires before them anyway.
  const v2 = (data?.settings as { meta_tracking_v2_enabled?: boolean } | undefined)?.meta_tracking_v2_enabled === true;
  const staff = v2 && isStaffPath(location.pathname);

  useEffect(() => {
    if (!data?.settings) return;
    if (staff) return;
    initPixels({
      facebookPixelId: data.settings.facebook_pixel_id,
      tiktokPixelId: data.settings.tiktok_pixel_id,
      ga4MeasurementId: data.settings.ga4_measurement_id,
      gtmContainerId: data.settings.gtm_container_id,
      clarityProjectId: data.settings.clarity_project_id,
    });
  }, [data?.settings, staff]);

  useEffect(() => {
    if (staff) return;
    trackPageView(location.pathname);
  }, [location.pathname, staff]);

  return null;
}

// Redirect legacy /editor/:id, /editor/:id/:idx, and /editor/:id/:idx/m
// routes into the canonical /flow/:id wizard. Preserves the search string
// (especially `?next=…` from the COD form) so the post-confirm redirect
// still lands on the right Shopify checkout URL.
function RedirectToFlow() {
  const { shopifyOrderId } = useParams<{ shopifyOrderId: string }>();
  const search = useLocation().search;
  if (!shopifyOrderId) return <Navigate to="/" replace />;
  return <Navigate to={`/flow/${shopifyOrderId}${search}`} replace />;
}

function AppRoutes() {
  const { init, loading } = useAuthStore();

  useEffect(() => {
    init();
  }, [init]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <PixelTracker />
      {/* Live notification toasts — Admin + Confirmation Agent only, and only on
          /admin pages: the feed self-gates on `orders.view` AND the route, so a
          customer page (preview, editor, upload, printer, flow) renders nothing
          and opens no channel even with a staff session in the browser
          (lib/useNotifications.ts, 2026-10-03). Mounted HERE rather than in
          AdminLayout because AdminLayout remounts on every admin page. */}
      <NotificationToasts />
      <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      {/* Magic upload link — token in URL is the access pass, no auth */}
      <Route path="/upload/:token" element={<MagicUploadPage />} />

      {/* PHASE 0 (temporary, internal): headless server-render probe. Probe-token
          gated; preview-only. Driven by the worker's robot browser to prove
          server-side PDF parity. Remove once the real server render is wired. */}
      <Route path="/_render-spike" element={<ServerRenderProbe />} />
      {/* Headless server-side flipbook-preview render (2026-07-08). Probe-token
          gated; rasterizes the STORED print PDFs into the previews bucket —
          the same job the admin "Generate previews" backfill did in-browser. */}
      <Route path="/_preview-spike" element={<PreviewRenderProbe />} />
      {/* The warm quick-3D page the VPS runner keeps open (2026-10-21, lib/quickPreview.ts). */}
      <Route path="/_quick-spike" element={<QuickRenderProbe />} />
      <Route path="/_pdf-intake" element={<PdfIntakeProbe />} />

      {/* Printer download link — token in URL, no auth. Multi-order ZIP + per-file. */}
      <Route path="/imprimeur/:token" element={<PrinterLinkPage />} />

      {/* Client flipbook preview — token in URL, no auth. 3D page-curl + notes + confirm. */}
      <Route path="/preview/:token" element={<PreviewBookPage />} />
      {/* Public cover catalog (owner, 2026-10-17) — OFF until app_settings.cover_catalog_enabled. */}
      <Route path="/couvertures" element={<CoverCatalogPage />} />
      {DevHomePreview && <Route path="/admin/_dev/home" element={<Suspense fallback={null}><DevHomePreview /></Suspense>} />}
      {DevAiProvidersPreview && <Route path="/admin/_dev/ai-providers" element={<Suspense fallback={null}><DevAiProvidersPreview /></Suspense>} />}
      {DevLivingPreview && <Route path="/admin/_dev/living" element={<Suspense fallback={null}><DevLivingPreview /></Suspense>} />}
      {DevCouponsPreview && <Route path="/admin/_dev/coupons" element={<Suspense fallback={null}><DevCouponsPreview /></Suspense>} />}
      {DevOutroPreview && <Route path="/admin/_dev/outro" element={<Suspense fallback={null}><DevOutroPreview /></Suspense>} />}

      {/* Customer */}
      {/*
        Root shows a resume card to a returning COD customer (not logged in)
        who has an in-progress order, otherwise the normal protected dashboard.
        ResumeLanding owns that branch; deep links (/flow/:id) skip it entirely.
      */}
      <Route path="/" element={<ResumeLanding />} />
      {/*
        Customer-facing flow — PUBLIC by design. The shopify_order_id in the
        URL is the access pass (unguessable 13-digit number). No login wall.
        See migration `anon_editor_access_via_order_id` for the matching RLS.
        Admin routes below stay protected.
      */}
      <Route path="/flow/:shopifyOrderId"                                element={<AlbumFlowPage />} />
      {/*
        Phase 2 cleanup (2026-05-13): /editor/:id and /editor/:id/:idx used
        to render the standalone CoverLibraryPage. That role moved into the
        wizard (StepDesign). Redirect bare-editor URLs into the wizard so
        old bookmarks resume cleanly. /covers stays mounted only for the
        editor's "Change cover" button until that becomes a modal in
        Phase 3. /m (AlbumMobilePage) is fully retired — mobile users now
        share /flow's preview step with desktop.
      */}
      <Route path="/editor/:shopifyOrderId"                              element={<RedirectToFlow />} />
      <Route path="/editor/:shopifyOrderId/:albumIndex"                  element={<RedirectToFlow />} />
      <Route path="/editor/:shopifyOrderId/:albumIndex/covers"           element={<CoverLibraryPage />} />
      <Route path="/editor/:shopifyOrderId/:albumIndex/photos"           element={<PhotoUploadPage />} />
      <Route path="/editor/:shopifyOrderId/:albumIndex/edit"             element={<AlbumEditorPage />} />
      <Route path="/editor/:shopifyOrderId/:albumIndex/m"                element={<RedirectToFlow />} />

      {/* Admin */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly perm="dashboard.view">
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route path="/admin/rapports" element={<ProtectedRoute adminOnly perm="dashboard.view"><AdminReportsPage /></ProtectedRoute>} />
      <Route
        path="/admin/orders"
        element={
          <ProtectedRoute adminOnly perm="orders.view">
            <AdminOrdersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/orders/:shopifyOrderId"
        element={
          <ProtectedRoute adminOnly perm="orders.view">
            <AdminOrderDetailPage />
          </ProtectedRoute>
        }
      />
      {/* Parcel scanner: packing → « Prêt à livrer », courier → « Expédiée » (2026-10-01).
          Same permission as the worker's /scan routes. */}
      <Route
        path="/admin/scanner"
        element={
          <ProtectedRoute adminOnly perm="orders.edit_status">
            <Suspense fallback={<PageSpinner />}>
              <AdminScannerPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customers"
        element={
          <ProtectedRoute adminOnly perm="customers.view">
            <AdminCustomersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customers/:id"
        element={
          <ProtectedRoute adminOnly>
            <AdminCustomerDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/covers"
        element={
          <ProtectedRoute adminOnly perm="covers.view">
            <AdminCoversPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/layouts"
        element={
          <ProtectedRoute adminOnly perm="templates.view">
            <AdminLayoutTemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/layouts/review"
        element={
          <ProtectedRoute adminOnly>
            <SlotReviewPane />
          </ProtectedRoute>
        }
      />
      {/* Coupons printed in the albums + promo codes (2026-10-28). */}
      <Route
        path="/admin/coupons"
        element={
          <ProtectedRoute adminOnly perm="coupons.manage">
            <Suspense fallback={<PageSpinner />}>
              <AdminCouponsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      {/* « Photo Vivante » — every album with the option (2026-11-03). */}
      <Route
        path="/admin/photo-vivante"
        element={
          <ProtectedRoute adminOnly perm="orders.view">
            <Suspense fallback={<PageSpinner />}>
              <AdminLivingPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/delivery"
        element={
          <ProtectedRoute adminOnly perm="delivery.manage">
            <AdminDeliveryAmeexPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/follow-up"
        element={
          <ProtectedRoute adminOnly perm="followups.view">
            <AdminFollowUpPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products"
        element={
          <ProtectedRoute adminOnly perm="products.manage">
            <AdminProductsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/stock"
        element={
          <ProtectedRoute adminOnly perm="stock.manage">
            <AdminStockPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/accounting"
        element={
          <ProtectedRoute adminOnly perm="accounting.view">
            <AdminAccountingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/impression"
        element={
          <ProtectedRoute adminOnly perm="production.print_batches">
            <AdminImpressionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <ProtectedRoute adminOnly perm="audit.view">
            <AdminAuditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute adminOnly>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/assistant"
        element={
          <ProtectedRoute adminOnly>
            <AdminAssistantPage />
          </ProtectedRoute>
        }
      />
      {/* Each user's own alert choices: push / WhatsApp / both / bell (2026-10-01). */}
      <Route
        path="/admin/notifications"
        element={
          <ProtectedRoute adminOnly perm="whatsapp.manage">
            <AdminNotificationsPage />
          </ProtectedRoute>
        }
      />
      {/* « Comparer les modèles » (owner, 2026-10-23) — admin only, like Settings. */}
      <Route path="/admin/ai-compare" element={<ProtectedRoute adminOnly><AdminAiComparePage /></ProtectedRoute>} />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute adminOnly>
            <AdminSettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/* The admin's app shell (2026-10-01): swipe-back, pull-to-refresh, route
          transitions, keep-where-I-was, admin-only viewport. Beside the routes,
          not in AdminLayout, because AdminLayout remounts on every page. Renders
          nothing and listens to nothing outside /admin. */}
      <AdminAppShell />
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </BrowserRouter>
      </LanguageProvider>
    </QueryClientProvider>
  );
}
